import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { toast } from 'react-hot-toast';
import { useLocalStorage } from '../helpers/useLocalStorage';
import { numberFormatter } from '../helpers/formatters';
import { LoadingIndicator } from '../helpers/LoadingIndicator';
import { InfiniteScrollObserver } from '../helpers/InfiniteScrollObserver';
import { useFlowSliceHistory, useTheme } from '../helpers/hooks';
import { colors, getContrastColor, interpolateColor } from '../utils/colors';
import { toDateString } from '../utils/dateUtils';
import { FlowSliceKey } from '@/types';
import { FlowSource } from '@/types/api/Flow';
import Plot from 'react-plotly.js';
import { Data } from 'plotly.js';
import { titleize } from '../utils/stringUtils';
import { Collapse } from 'bootstrap';
import { HorizontalDivider } from '../helpers/HorizontalDivider';
import { ChevronDownIcon, ChevronUpIcon } from '@iconicicons/react';

const START_COLOR = '#0096FF';
const END_COLOR = '#FF0000';

// we can either create nomCycle columns based on data, or hard code columns - either way we should force all nom cycles to be consistent name and order in the backend
const NOM_CYCLES: ['T', 'E', 'I1', 'I2', 'I3'] = ['T', 'E', 'I1', 'I2', 'I3'];
const CYCLE_COLORS = {
  T: '#0096FF',
  E: '#00FF00',
  I1: '#FFFF00',
  I2: '#FFA500',
  I3: '#FF0000',
};

export type CapacityComponentHistoryProps = {
  componentId?: number | false;
  flowSliceKey?: FlowSliceKey | null;
  style?: React.CSSProperties;
  className?: string;
};

/**
 * Component for displaying flow history.
 * Supports two modes:
 * - Legacy: Pass componentId (number) to use GET-based history endpoint
 * - New: Pass flowSliceKey (object) to use POST-based history endpoint
 *
 * @param {CapacityComponentHistoryProps} props
 */
export const CapacityComponentHistory: React.FC<
  CapacityComponentHistoryProps
> = ({ componentId, flowSliceKey, style, className }) => {
  const [theme] = useTheme();
  const [chartOpen, setChartOpen] = useState(true);

  const collapseRef = useRef<HTMLDivElement>(null);
  const [collapse, setCollapse] = useState<Collapse | null>(null);

  const chartContainerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!collapseRef.current) return;
    const c = new Collapse(collapseRef.current, {
      toggle: false,
    });
    setCollapse(c);
  }, [collapseRef.current]);

  useEffect(() => {
    if (chartOpen) {
      collapse?.show();
    }
  }, [collapse]);

  const toggleChart = useCallback(() => {
    if (chartOpen) collapse?.hide();
    else collapse?.show();
    setChartOpen(!chartOpen);
  }, [collapse, chartOpen]);

  // TODO: should be able to select the source (platts, notices, pointlogic, etc)

  const itemsPerPage = 30;

  const _flowSliceKey = useMemo<FlowSliceKey>(
    () =>
      flowSliceKey ?? {
        component_id: Number(componentId),
        provider: 'CRITERION',
        normalized_provider_key: null,
        view: 'net',
      },
    [flowSliceKey, componentId],
  );

  // New hook (POST-based)
  const result = useFlowSliceHistory({
    flowSliceKey: _flowSliceKey,
    itemsPerPage,
  });

  // Use the appropriate result based on which mode we're in
  const {
    capacityComponent,
    flows: records,
    flowsLoading: capacityComponentLoading,
    error,
    flowsFetching,
    exhausted,
    flowsLoadError: capacityComponentError,
    fetchNextPage,
  } = result;
  const [concept, setConcept] = useLocalStorage<keyof FlowSource>(
    'concept',
    'utilization',
  );

  const getErrorMessage = (err: any) => {
    if (!err) return 'Failed to load flow history';
    if (typeof err === 'string') return err;
    if (typeof err?.data === 'string') return err.data;
    if (typeof err?.data?.error === 'string') return err.data.error;
    if (typeof err?.data?.detail === 'string') return err.data.detail;
    if (typeof err?.error === 'string') return err.error;
    return 'Failed to load flow history';
  };

  const getColor = (concept: string, record?: FlowSource) => {
    if (record === null || record === undefined) {
      return 'transparent';
    }
    if (concept === 'interruptible_flow') {
      if (record.interruptible_flow === 'Y') return START_COLOR;
      if (record.interruptible_flow === 'N') return END_COLOR;
    }
    return interpolateColor(
      START_COLOR,
      END_COLOR,
      Math.min(record.ratio / 100, 1),
    );
  };

  const gasDates = [...new Set(records.map((record) => record.gas_date))];

  const chartData = useMemo(() => {
    return NOM_CYCLES.map<Data>((nomCycle) => {
      const values = gasDates.map((gasDate) => {
        const record = records.find(
          (record) =>
            record.gas_date === gasDate && record.nomination_cycle === nomCycle,
        );
        const value =
          Number(record?.[concept] as number | string | null) *
          (record?.direction_sign ?? 1);
        return value ?? null;
      });
      return {
        x: gasDates,
        y: values,
        type: 'scatter',
        mode: 'lines+markers',
        marker: { color: CYCLE_COLORS[nomCycle] },
        name: nomCycle,
      };
    });
  }, [records, concept, gasDates, colors]);

  const chartLayout = useMemo<Partial<Plotly.Layout>>(() => {
    const conceptTitle = titleize(concept.replace(/_/g, ' '));
    return {
      width: 900,
      plot_bgcolor: colors.themes[theme].secondaryBg,
      paper_bgcolor: colors.themes[theme].body,
      title: { text: `${conceptTitle} Over Time` },
      font: { color: colors.themes[theme].color },
      xaxis: { title: { text: 'Gas Date' }, color: colors.themes[theme].color },
      yaxis: {
        title: { text: conceptTitle },
        color: colors.themes[theme].color,
      },
    };
  }, [theme, colors, concept]);

  useEffect(() => {
    if (capacityComponentError) {
      console.error(error);
      toast.error(getErrorMessage(error));
    }
  }, [capacityComponentError, error]);

  if (flowsFetching && !capacityComponent) {
    return <LoadingIndicator className="my-5" style={{ overflow: 'hidden' }} />;
  }

  if (!capacityComponent || capacityComponentError) {
    return null;
  }
  // what's the best way to format outputs?  Would be great to do it all in one place.  I tried in the backend serializers, but it caused a problem because turning the numbers into strings means math doesn't work.  Maybe we should send a formatted and non formatted value from backend?
  // should put this function somewhere to be called from other components
  const formatConcept = (
    concept: string,
    value?: number | string | number[] | null,
    sign: number = 1,
  ): string => {
    if (Array.isArray(value)) {
      return value.map((v) => formatConcept(concept, v, sign)).join(', ');
    }
    if (value === 0) {
      sign = 1; // to avoid showing -0
    }
    if (value === null || value === undefined) {
      return '-';
    }
    if (['scheduled_volume', 'opcap', 'oac'].includes(concept)) {
      return numberFormatter(sign * Number(value), 0);
    }
    if (concept === 'utilization') {
      return `${numberFormatter(100 * sign * Number(value), 0)}%`;
    }
    if (concept === 'utilization') {
      return `${numberFormatter(sign * Number(value), 0)}%`;
    }
    return value.toString(); // for interruptible_flow, we just return the value as is, which is 'Y' or 'N'
  };

  return (
    <div style={style} className={className}>
      {/* could select source, but also could have indicator that a pipeline notice exists for a given date / cycle when looking at platts source */}
      {/* <select value={source} onChange={evt => setSource(evt.target.value)} >
        {Object.keys(capacityComponent.records).map(source => (
          <option value={source}>{source}</option>)
        )}
      </select> */}
      <div
        style={{
          position: 'sticky',
          top: -16,
          backgroundColor: 'var(--bs-body-bg)',
          width: 'calc(100% + 32px)',
          zIndex: 2,
          margin: '0 -16px 0 -16px',
          padding: '16px',
        }}
        ref={chartContainerRef}
      >
        <select
          className="form-select"
          value={concept}
          onChange={(evt) => setConcept(evt.target.value as keyof FlowSource)}
        >
          <option value="utilization">Utilization</option>
          <option value="oac">OAC</option>
          <option value="scheduled_volume">Scheduled Volume</option>
          <option value="opcap">Op Cap</option>
          <option value="interruptible_flow">Interruptible Flow?</option>
        </select>
      </div>
      <table
        className="table table-striped mt-3"
        style={{ position: 'relative', overflowY: 'scroll' }}
      >
        <thead
          style={{
            position: 'sticky',
            top: (chartContainerRef.current?.offsetHeight ?? 0) - 16,
            backgroundColor: 'var(--bs-body-bg)',
          }}
        >
          <tr>
            <th colSpan={NOM_CYCLES.length + 1} style={{ borderBottom: '0px' }}>
              <div ref={collapseRef}>
                <Plot
                  className="d-flex justify-content-center align-items-center mt-3"
                  data={chartData}
                  layout={chartLayout}
                />
              </div>
              <HorizontalDivider onClick={toggleChart}>
                {chartOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
              </HorizontalDivider>
            </th>
          </tr>
          <tr>
            <th className="text-end" style={{ maxWidth: '30px' }}>
              Gas Date
            </th>
            {NOM_CYCLES.map((nomCycle) => (
              <th key={nomCycle} className="text-center">
                {nomCycle}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {gasDates.map((gasDate) => {
            return (
              <tr key={gasDate}>
                <th className="text-end" style={{ width: '60px' }}>
                  {toDateString(gasDate)}
                </th>
                {NOM_CYCLES.map((nomCycle) => {
                  const record = records.find(
                    (record) =>
                      record.gas_date === gasDate &&
                      record.nomination_cycle === nomCycle,
                  );
                  const bgColor = getColor(concept, record);
                  return (
                    <td
                      key={`${gasDate}-${nomCycle}-${concept}`}
                      className="text-center"
                      style={{
                        backgroundColor: bgColor,
                        color:
                          bgColor === 'transparent'
                            ? 'currentColor'
                            : getContrastColor(bgColor),
                      }}
                    >
                      {formatConcept(
                        concept,
                        record?.[concept],
                        record?.direction_sign,
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
      {flowsFetching || capacityComponentLoading ? (
        <LoadingIndicator className="my-3" style={{ overflow: 'hidden' }} />
      ) : (
        !exhausted && <InfiniteScrollObserver onScrollIn={fetchNextPage} />
      )}
    </div>
  );
};
